import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { debounce } from 'lodash';
import rootAxios from 'requests/rootAxios';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  Chip,
  Stack,
  CircularProgress,
  Divider,
  Checkbox,
  Tab,
  Tabs,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BlockIcon from '@mui/icons-material/Block';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  fetchTrailblazeConfigs,
  fetchAllCircles,
  saveTrailblazeConfig,
  toggleTrailblazeConfig,
  deleteTrailblazeConfig,
} from 'reducer/trainingConfigSlice';
import {
  fetchPFConfigs,
  savePFConfig,
  togglePFConfig,
  deletePFConfig,
} from 'reducer/productFinancingConfigSlice';
import {
  fetchTrainingBypasses,
  createTrainingBypass,
  deleteTrainingBypass,
} from 'reducer/trainingBypassSlice';
import { fetchAllTrainers, fetchAllTrainingTypes } from 'thunks/trainingThunks/thunk';
import TableComponent from 'Component/Table';

const TRAINING_TYPE = 'Trailblaze_2.0';

// ── Training Bypass Modal ───────────────────────────────────────────────────
const BypassModal = ({ open, onClose, onSave, trainingTypeOptions }) => {
  const [employeeId, setEmployeeId] = useState('');
  const [trainingType, setTrainingType] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEmployeeId('');
      setTrainingType('');
      setReason('');
      setFile(null);
    }
  }, [open]);

  const handleSave = async () => {
    if (!employeeId.trim()) return toast.error('Employee ID is required');
    if (!trainingType) return toast.error('Training type is required');
    if (!reason.trim()) return toast.error('Reason is required');
    if (!file) return toast.error('Attachment is required');

    const formData = new FormData();
    formData.append('employeeId', employeeId.trim());
    formData.append('trainingType', trainingType);
    formData.append('reason', reason.trim());
    formData.append('file', file);

    setSaving(true);
    try {
      await onSave(formData);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Add Training Bypass
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Exclude an employee from a training type's pendency
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Employee ID"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            size="small"
            fullWidth
            required
          />
          <FormControl size="small" fullWidth required>
            <InputLabel>Training Type</InputLabel>
            <Select
              value={trainingType}
              label="Training Type"
              onChange={(e) => setTrainingType(e.target.value)}
            >
              {(trainingTypeOptions || []).map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            size="small"
            fullWidth
            multiline
            rows={3}
            required
          />
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<AttachFileIcon />}
              size="small"
            >
              {file ? file.name : 'Upload Attachment *'}
              <input type="file" hidden onChange={(e) => setFile(e.target.files[0] || null)} />
            </Button>
            {file && (
              <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                {(file.size / 1024).toFixed(1)} KB
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={<BlockIcon />}
        >
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Add Bypass
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Config Form Modal ───────────────────────────────────────────────────────
const ConfigModal = ({
  open,
  onClose,
  existingConfig,
  trainers,
  circles,
  circlesLoading,
  onSave,
}) => {
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [selectedCircles, setSelectedCircles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (existingConfig) {
        setSelectedTrainer(trainers.find((t) => t.employeeId === existingConfig.trainerId) || null);
        setSelectedCircles(existingConfig.assignedCircles || []);
      } else {
        setSelectedTrainer(null);
        setSelectedCircles([]);
      }
    }
  }, [open, existingConfig, trainers]);

  const handleSave = async () => {
    if (!selectedTrainer) {
      toast.error('Please select a trainer');
      return;
    }
    if (selectedCircles.length === 0) {
      toast.error('Please assign at least one circle');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        trainerId: selectedTrainer.employeeId,
        trainerName: selectedTrainer.employeeName,
        trainingType: TRAINING_TYPE,
        assignedCircles: selectedCircles,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Not used — kept to avoid breaking any future consumers
  const circlesByRegion = useMemo(() => {
    const map = {};
    circles.forEach((c) => {
      (c.regions || []).forEach(({ regionName }) => {
        if (!map[regionName]) map[regionName] = [];
        if (!map[regionName].find((x) => x.circleId === c.circleId)) map[regionName].push(c);
      });
    });
    return map;
  }, [circles]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {existingConfig ? 'Edit Configuration' : 'Add Trainer Configuration'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Assign circles to a trainer for Trailblaze 2.0 training
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Trainer selector */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Trainer
          </Typography>
          <Autocomplete
            options={trainers}
            getOptionLabel={(o) => `${o.employeeName} (${o.employeeId})`}
            value={selectedTrainer}
            onChange={(_, v) => setSelectedTrainer(v)}
            disabled={!!existingConfig}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search trainer..." size="small" fullWidth />
            )}
          />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Circle multi-select */}
        <Box>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Assigned Circles
            {selectedCircles.length > 0 && (
              <Chip
                label={`${selectedCircles.length} selected`}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>

          {circlesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Autocomplete
              multiple
              options={circles}
              getOptionLabel={(o) => `${o.circleName} (${o.circleId})`}
              value={selectedCircles}
              onChange={(_, v) => setSelectedCircles(v)}
              isOptionEqualToValue={(opt, val) => opt.circleId === val.circleId}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} key={option.circleId}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1, p: 0 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.circleName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {option.circleId}
                      {option.regions?.length
                        ? ` · ${option.regions.map((r) => r.regionName).join(', ')}`
                        : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search by circle name or ID..."
                  size="small"
                  fullWidth
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.circleId}
                    label={`${option.circleName} (${option.circleId})`}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              PaperComponent={({ children, ...paperProps }) => {
                const allSelected = selectedCircles.length === circles.length;
                return (
                  <Box
                    {...paperProps}
                    sx={{ boxShadow: 3, borderRadius: 1, bgcolor: 'background.paper' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        py: 0.75,
                        borderBottom: '1px solid #e2e8f0',
                      }}
                    >
                      <Typography variant="caption" color="textSecondary">
                        {selectedCircles.length} of {circles.length} selected
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setSelectedCircles(allSelected ? [] : circles)}
                        sx={{ textTransform: 'none', minWidth: 'auto', py: 0 }}
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    </Box>
                    <Box sx={{ bgcolor: 'background.paper' }}>{children}</Box>
                  </Box>
                );
              }}
            />
          )}

          {/* Summary of selected circles grouped by region */}
          {selectedCircles.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                Selected by region:
              </Typography>
              {Object.entries(
                selectedCircles.reduce((acc, c) => {
                  (c.regions || []).forEach(({ regionName }) => {
                    if (!acc[regionName]) acc[regionName] = [];
                    if (!acc[regionName].includes(c.circleName)) acc[regionName].push(c.circleName);
                  });
                  return acc;
                }, {})
              ).map(([region, cNames]) => (
                <Box key={region} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    {region}:{' '}
                  </Typography>
                  <Typography variant="caption">{cNames.join(', ')}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {existingConfig ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Reusable debounced approver search autocomplete ─────────────────────────
const ApproverSearchAutocomplete = ({ label, approvers, onAdd, onRemove }) => {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const fetchEmployees = useCallback(
    debounce(async (search) => {
      if (!search || search.trim().length < 2) {
        setOptions([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        const res = await rootAxios.post('/super-user/getEmployee', { searchString: search });
        setOptions(res?.data?.data?.employee || []);
      } catch {
        setOptions([]);
      } finally {
        setSearching(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (_, value) => {
    setInputValue(value);
    fetchEmployees(value);
  };

  const handleSelect = (_, employee) => {
    if (!employee) return;
    if (approvers.find((a) => a.empId === employee.employeeId)) {
      toast.warning('Approver already added');
      return;
    }
    onAdd({ empId: employee.employeeId, empName: employee.employeeName });
    setInputValue('');
    setOptions([]);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {label}
      </Typography>

      <Autocomplete
        options={options}
        getOptionLabel={(o) => `${o.employeeName} (${o.employeeId})`}
        filterOptions={(x) => x} // disable client-side filter — server handles it
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleSelect}
        value={null} // always reset after selection
        loading={searching}
        noOptionsText={
          inputValue.length < 2 ? 'Type at least 2 characters...' : 'No employees found'
        }
        isOptionEqualToValue={(o, v) => o.employeeId === v?.employeeId}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Search by name or employee ID..."
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {searching ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Selected approver chips */}
      <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
        {approvers.length === 0 ? (
          <Typography variant="caption" color="textSecondary">
            None selected yet
          </Typography>
        ) : (
          approvers.map((a) => (
            <Chip
              key={a.empId}
              label={`${a.empName} (${a.empId})`}
              size="small"
              color="primary"
              variant="outlined"
              onDelete={() => onRemove(a.empId)}
            />
          ))
        )}
      </Stack>
    </Box>
  );
};

// ── PF Config Modal ─────────────────────────────────────────────────────────
const PFConfigModal = ({ open, onClose, existingConfig, trainers, onSave }) => {
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [l1Approvers, setL1Approvers] = useState([]);
  const [l2Approvers, setL2Approvers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (existingConfig) {
        setSelectedTrainer(trainers.find((t) => t.employeeId === existingConfig.trainerId) || null);
        setL1Approvers(existingConfig.l1Approvers || []);
        setL2Approvers(existingConfig.l2Approvers || []);
      } else {
        setSelectedTrainer(null);
        setL1Approvers([]);
        setL2Approvers([]);
      }
    }
  }, [open, existingConfig, trainers]);

  const handleSave = async () => {
    if (!selectedTrainer) {
      toast.error('Please select a trainer');
      return;
    }
    if (l1Approvers.length === 0) {
      toast.error('Add at least one L1 approver');
      return;
    }
    if (l2Approvers.length === 0) {
      toast.error('Add at least one L2 approver');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        trainerId: selectedTrainer.employeeId,
        trainerName: selectedTrainer.employeeName,
        trainingType: 'Product_Financing',
        l1Approvers,
        l2Approvers,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          {existingConfig ? 'Edit PF Configuration' : 'Add Product Financing Configuration'}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Configure trainer and L1/L2 approvers for Product Financing training
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Trainer */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Trainer
          </Typography>
          <Autocomplete
            options={trainers}
            getOptionLabel={(o) => `${o.employeeName} (${o.employeeId})`}
            value={selectedTrainer}
            onChange={(_, v) => setSelectedTrainer(v)}
            disabled={!!existingConfig}
            renderInput={(params) => (
              <TextField {...params} placeholder="Search trainer..." size="small" fullWidth />
            )}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <ApproverSearchAutocomplete
          label="L1 Approvers (per-trainee review)"
          approvers={l1Approvers}
          onAdd={(a) => setL1Approvers((prev) => [...prev, a])}
          onRemove={(empId) => setL1Approvers((prev) => prev.filter((a) => a.empId !== empId))}
        />

        <Divider sx={{ mb: 2 }} />

        <ApproverSearchAutocomplete
          label="L2 Approvers (final batch approval)"
          approvers={l2Approvers}
          onAdd={(a) => setL2Approvers((prev) => [...prev, a])}
          onRemove={(empId) => setL2Approvers((prev) => prev.filter((a) => a.empId !== empId))}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {existingConfig ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const TrainingConfiguration = () => {
  const dispatch = useDispatch();
  const {
    configs: tbConfigs,
    circles,
    loading: tbLoading,
    circlesLoading,
  } = useSelector((s) => s.trainingConfigSlice);
  const { configs: pfConfigs, loading: pfLoading } = useSelector((s) => s.pfConfigSlice);
  const { bypasses, loading: bypassLoading } = useSelector((s) => s.trainingBypassSlice);
  const {
    data: { trainers },
  } = useSelector((s) => s.trainersSlice);
  const { trainingTypes } = useSelector((s) => s.trainingTypePageSlice.data);

  const [activeTab, setActiveTab] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [bypassModalOpen, setBypassModalOpen] = useState(false);
  const [bypassDeleteTarget, setBypassDeleteTarget] = useState(null);
  const [bypassDeleting, setBypassDeleting] = useState(false);

  useEffect(() => {
    dispatch(fetchTrailblazeConfigs());
    dispatch(fetchAllCircles());
    dispatch(fetchPFConfigs());
    dispatch(fetchAllTrainers({ skip: 0, limit: 1000 }));
    dispatch(fetchTrainingBypasses());
    dispatch(fetchAllTrainingTypes());
  }, [dispatch]);

  // ── Trailblaze handlers ────────────────────────────────────────────────
  const handleTBSave = async (payload) => {
    const result = await dispatch(saveTrailblazeConfig(payload));
    if (saveTrailblazeConfig.fulfilled.match(result)) {
      toast.success('Configuration saved successfully');
      dispatch(fetchTrailblazeConfigs());
    } else {
      toast.error(result.payload || 'Failed to save configuration');
      throw new Error(result.payload);
    }
  };

  const handleTBToggle = async (config, newValue) => {
    const result = await dispatch(
      toggleTrailblazeConfig({
        trainerId: config.trainerId,
        trainingType: config.trainingType,
        isActive: newValue,
      })
    );
    if (!toggleTrailblazeConfig.fulfilled.match(result)) toast.error('Failed to update status');
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const isTrailblazeTarget = deleteTarget.trainingType === 'Trailblaze_2.0';
      const action = isTrailblazeTarget ? deleteTrailblazeConfig : deletePFConfig;
      const result = await dispatch(
        action({
          trainerId: deleteTarget.trainerId,
          trainingType: deleteTarget.trainingType,
        })
      );
      if (action.fulfilled.match(result)) {
        toast.success('Configuration deleted');
      } else {
        toast.error(result.payload || 'Failed to delete configuration');
      }
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // ── PF handlers ───────────────────────────────────────────────────────
  const handlePFSave = async (payload) => {
    const result = await dispatch(savePFConfig(payload));
    if (savePFConfig.fulfilled.match(result)) {
      toast.success('PF Configuration saved successfully');
      dispatch(fetchPFConfigs());
    } else {
      toast.error(result.payload || 'Failed to save PF configuration');
      throw new Error(result.payload);
    }
  };

  const handlePFToggle = async (config, newValue) => {
    const result = await dispatch(
      togglePFConfig({
        trainerId: config.trainerId,
        trainingType: config.trainingType,
        isActive: newValue,
      })
    );
    if (!togglePFConfig.fulfilled.match(result)) toast.error('Failed to update PF status');
  };

  // ── Bypass handlers ────────────────────────────────────────────────────
  const handleBypassSave = async (formData) => {
    const result = await dispatch(createTrainingBypass(formData));
    if (createTrainingBypass.fulfilled.match(result)) {
      toast.success('Bypass added successfully');
      dispatch(fetchTrainingBypasses());
    } else {
      toast.error(result.payload || 'Failed to add bypass');
      throw new Error(result.payload);
    }
  };

  const handleBypassDelete = async () => {
    if (!bypassDeleteTarget) return;
    setBypassDeleting(true);
    try {
      const result = await dispatch(deleteTrainingBypass(bypassDeleteTarget._id));
      if (deleteTrainingBypass.fulfilled.match(result)) {
        toast.success('Bypass removed');
      } else {
        toast.error(result.payload || 'Failed to remove bypass');
      }
    } finally {
      setBypassDeleting(false);
      setBypassDeleteTarget(null);
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────
  const tbColumns = [
    { label: 'Trainer Name', field: 'trainerName' },
    { label: 'Trainer ID', field: 'trainerId' },
    {
      label: 'Assigned Circles',
      field: 'assignedCircles',
      render: (row) => (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(row.assignedCircles || []).length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              None
            </Typography>
          ) : (
            row.assignedCircles.map((c) => (
              <Chip key={c.circleId} label={c.circleName} size="small" variant="outlined" />
            ))
          )}
        </Stack>
      ),
    },
    {
      label: 'Regions',
      field: 'regions',
      render: (row) => {
        const uniqueRegions = [
          ...new Set(
            (row.assignedCircles || []).flatMap((c) =>
              c.regions ? c.regions.map((r) => r.regionName) : [c.regionName].filter(Boolean)
            )
          ),
        ];
        return <Typography variant="body2">{uniqueRegions.join(', ') || '—'}</Typography>;
      },
    },
    {
      label: 'Active',
      field: 'isActive',
      render: (row) => (
        <Switch
          checked={!!row.isActive}
          onChange={(e) => handleTBToggle(row, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      label: 'Action',
      field: 'edit',
      render: (row) => (
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setEditingConfig(row);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const pfColumns = [
    { label: 'Trainer Name', field: 'trainerName' },
    { label: 'Trainer ID', field: 'trainerId' },
    {
      label: 'L1 Approvers',
      field: 'l1Approvers',
      render: (row) => (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(row.l1Approvers || []).length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              None
            </Typography>
          ) : (
            row.l1Approvers.map((a) => (
              <Chip
                key={a.empId}
                label={`${a.empName} (${a.empId})`}
                size="small"
                color="info"
                variant="outlined"
              />
            ))
          )}
        </Stack>
      ),
    },
    {
      label: 'L2 Approvers',
      field: 'l2Approvers',
      render: (row) => (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {(row.l2Approvers || []).length === 0 ? (
            <Typography variant="caption" color="textSecondary">
              None
            </Typography>
          ) : (
            row.l2Approvers.map((a) => (
              <Chip
                key={a.empId}
                label={`${a.empName} (${a.empId})`}
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))
          )}
        </Stack>
      ),
    },
    {
      label: 'Active',
      field: 'isActive',
      render: (row) => (
        <Switch
          checked={!!row.isActive}
          onChange={(e) => handlePFToggle(row, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      label: 'Action',
      field: 'edit',
      render: (row) => (
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              setEditingConfig(row);
              setModalOpen(true);
            }}
          >
            Edit
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </Button>
        </Stack>
      ),
    },
  ];

  const bypassColumns = [
    { label: 'Employee ID', field: 'employeeId' },
    { label: 'Training Type', field: 'trainingType' },
    { label: 'Reason', field: 'reason' },
    { label: 'Added By', field: 'addedByName' },
    {
      label: 'Attachment',
      field: 'attachmentUrl',
      render: (row) =>
        row.attachmentUrl ? (
          <Tooltip title="View attachment">
            <IconButton
              size="small"
              color="primary"
              onClick={() => window.open(row.attachmentUrl, '_blank', 'noopener,noreferrer')}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="caption" color="textSecondary">
            —
          </Typography>
        ),
    },
    {
      label: 'Added On',
      field: 'createdAt',
      render: (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString('en-IN') : '—'),
    },
    {
      label: 'Action',
      field: 'delete',
      render: (row) => (
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setBypassDeleteTarget(row)}
        >
          Remove
        </Button>
      ),
    },
  ];

  const isTrailblaze = activeTab === 0;
  const isBypass = activeTab === 2;

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="h5" fontWeight="600" sx={{ color: '#1e293b', mb: 0.25 }}>
                Training Configuration
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                {isTrailblaze
                  ? 'Assign trainers and circles for Trailblaze 2.0'
                  : isBypass
                    ? 'Exclude specific employees from training pendency'
                    : 'Configure trainers and approvers for Product Financing'}
              </Typography>
            </Box>
            {isBypass ? (
              <Button
                variant="contained"
                startIcon={<BlockIcon />}
                onClick={() => setBypassModalOpen(true)}
              >
                Add Bypass
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingConfig(null);
                  setModalOpen(true);
                }}
              >
                Add Configuration
              </Button>
            )}
          </Box>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(_, v) => {
              setActiveTab(v);
              setEditingConfig(null);
              setModalOpen(false);
            }}
            sx={{ mt: 1 }}
          >
            <Tab label="Trailblaze 2.0" />
            <Tab label="Product Financing" />
            <Tab label="Training Bypass" />
          </Tabs>
        </Box>

        {/* Table */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isTrailblaze ? (
              <TableComponent
                tableData={tbConfigs}
                columns={tbColumns}
                tableName="TrailblazeConfiguration"
                count={tbConfigs.length}
                useWholeRowClicked={false}
                enableLocalSearch={true}
              />
            ) : isBypass ? (
              <TableComponent
                tableData={bypasses}
                columns={bypassColumns}
                tableName="TrainingBypass"
                count={bypasses.length}
                useWholeRowClicked={false}
                enableLocalSearch={true}
              />
            ) : (
              <TableComponent
                tableData={pfConfigs}
                columns={pfColumns}
                tableName="PFConfiguration"
                count={pfConfigs.length}
                useWholeRowClicked={false}
                enableLocalSearch={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Delete config confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Configuration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the configuration for{' '}
            <strong>{deleteTarget?.trainerName}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={deleting}>
            {deleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete bypass confirmation dialog */}
      <Dialog
        open={!!bypassDeleteTarget}
        onClose={() => !bypassDeleting && setBypassDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove Bypass</DialogTitle>
        <DialogContent>
          <Typography>
            Remove bypass for employee <strong>{bypassDeleteTarget?.employeeId}</strong> (
            {bypassDeleteTarget?.trainingType})? They will re-appear in training pendency.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setBypassDeleteTarget(null)}
            color="inherit"
            disabled={bypassDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBypassDelete}
            variant="contained"
            color="error"
            disabled={bypassDeleting}
          >
            {bypassDeleting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      {/* Config Modals */}
      {isTrailblaze ? (
        <ConfigModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          existingConfig={editingConfig}
          trainers={trainers || []}
          circles={circles || []}
          circlesLoading={circlesLoading}
          onSave={handleTBSave}
        />
      ) : (
        <PFConfigModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          existingConfig={editingConfig}
          trainers={trainers || []}
          onSave={handlePFSave}
        />
      )}

      {/* Bypass Modal */}
      <BypassModal
        open={bypassModalOpen}
        onClose={() => setBypassModalOpen(false)}
        onSave={handleBypassSave}
        trainingTypeOptions={(trainingTypes || []).map((t) => t.trainingName)}
      />
    </Layout>
  );
};

export default TrainingConfiguration;
